/** Adapter between the API subscription (raw Prisma) and the UI `Subscription`. */

import type { Currency, SubFrequency, SubStatus, Subscription } from "@/types";

export interface ApiSubscription {
  id: string;
  name: string;
  provider: string;
  cost: number | string;
  currency?: Currency;
  frequency: SubFrequency;
  renewalDate?: string | null;
  status: SubStatus;
}

export interface SubscriptionInput {
  name: string;
  provider: string;
  cost: number;
  frequency: SubFrequency;
  renewalDate?: string;
}

function toNumber(value: number | string | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(n) ? n : 0;
}

/** ISO datetime → "YYYY-MM-DD" (matches the mock date style). */
function formatDate(raw: string | null | undefined): string {
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "—";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function toSubscription(api: ApiSubscription): Subscription {
  return {
    id: api.id,
    name: api.name,
    provider: api.provider,
    cost: toNumber(api.cost),
    currency: api.currency ?? "USD",
    frequency: api.frequency,
    renewalDate: formatDate(api.renewalDate),
    status: api.status,
  };
}

export function toSubscriptionWriteBody(input: SubscriptionInput): Record<string, unknown> {
  return {
    name: input.name,
    provider: input.provider,
    cost: input.cost,
    currency: "USD",
    frequency: input.frequency,
    ...(input.renewalDate ? { renewalDate: input.renewalDate } : {}),
    status: "ACTIVE",
  };
}

export function toMockSubscription(input: SubscriptionInput): Subscription {
  return {
    id: `s_${crypto.randomUUID().slice(0, 8)}`,
    name: input.name,
    provider: input.provider,
    cost: input.cost,
    currency: "USD",
    frequency: input.frequency,
    renewalDate: input.renewalDate || "—",
    status: "ACTIVE",
  };
}
